"use client";

import React from "react";
import { Users, House, BookOpen, Sparkles, ArrowRight } from "lucide-react";
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
import { Card, CardHeader, CardTitle, CardDescription, CardFooter } from "@/components/ui/card";
const pathways = [{
  id: 1,
  title: "Ride-Hailing",
  description: "Seamless urban mobility with professional drivers at your fingertips.",
  icon: "Users",
  link: "/HomePage"
}, {
  id: 2,
  title: "Premium Black",
  description: "The gold standard of luxury transport for your most important journeys.",
  icon: "Sparkles",
  link: "/HomePage"
}, {
  id: 3,
  title: "Corporate Mobility",
  description: "Tailored logistics solutions for businesses and executive teams.",
  icon: "BookOpen",
  link: "/HomePage"
}];
const highlights = [{
  pathwayId: 1,
  text: "Real-time Tracking"
}, {
  pathwayId: 1,
  text: "Verified Drivers"
}, {
  pathwayId: 1,
  text: "Transparent Pricing"
}, {
  pathwayId: 2,
  text: "Luxury Sedans"
}, {
  pathwayId: 2,
  text: "Concierge Service"
}, {
  pathwayId: 2,
  text: "Priority Dispatch"
}, {
  pathwayId: 3,
  text: "Monthly Invoicing"
}, {
  pathwayId: 3,
  text: "Event Logistics"
}, {
  pathwayId: 3,
  text: "Airport Meet & Greet"
}];
const iconMap: Record<string, React.ElementType> = {
  Users,
  House,
  BookOpen,
  Sparkles
};
export default function LearningPathways() {
  return <section data-section-id="224341" id="tailored-transport-solutions" className="relative w-full bg-background py-24 overflow-hidden">
      {}
      <div className="absolute inset-0 bg-[linear-gradient(to_right,var(--color-border)_1px,transparent_1px),linear-gradient(to_bottom,var(--color-border)_1px,transparent_1px)] bg-[size:4rem_4rem] [mask-image:radial-gradient(ellipse_60%_50%_at_50%_0%,#000_70%,transparent_100%)] opacity-[0.03]" />

      <div className="container mx-auto px-4 md:px-6 relative z-10">
        <div className="max-w-3xl mx-auto text-center mb-16">
          <h2 className="text-3xl md:text-5xl font-bold font-default text-foreground mb-6 tracking-tight animate-slide-up">
            Tailored Transport Solutions
          </h2>
          <p className="text-lg text-muted-foreground font-sans animate-slide-up delay-100">
            Experience the next evolution of mobility. From daily commutes to executive travel, Moveonix delivers premium reliability in every mile.
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 md:gap-6 lg:gap-8 max-w-6xl mx-auto overflow-hidden">
          {pathways.map((pathway, index) => {
          const Icon = iconMap[pathway.icon];
          const pathwayHighlights = highlights.filter(h => h.pathwayId === pathway.id);
          const isPremium = pathway.title === "Premium Black";
          const delayClass = index === 0 ? "delay-100" : index === 1 ? "delay-200" : "delay-300";
          return <div key={pathway.id} data-index={index} className={`group relative rounded-md p-[1px] ${isPremium ? 'bg-gradient-to-b from-primary/40 to-transparent' : 'bg-gradient-to-b from-border/50 to-transparent'} overflow-hidden animate-slide-up ${delayClass}`}>
                {}
                <div className={`absolute inset-0 ${isPremium ? 'bg-primary/5' : 'bg-secondary/0'} group-hover:bg-primary/5 transition-colors duration-700 blur-2xl`} />

                <Card className={`relative h-full min-h-[420px] bg-card backdrop-blur-xl border border-border/40 rounded-md transition-all duration-500 group-hover:scale-[1.04] group-hover:shadow-md group-hover:ring-2 group-hover:ring-primary/30 flex flex-col overflow-hidden`}>
                  
                  {}
                  <CardHeader className="flex-1 relative z-10 transition-opacity duration-500 group-hover:opacity-0">
                    <div className={`size-16 rounded-md bg-muted flex items-center justify-center mb-6 border border-border transition-all duration-300 group-hover:border-primary/50 ${isPremium ? 'shadow-[0_0_15px_rgba(201,161,90,0.2)]' : ''}`}>
                      <Icon className={`size-8 ${isPremium ? 'text-primary' : 'text-foreground'} transition-all duration-300 group-hover:rotate-[5deg] group-hover:text-primary`} strokeWidth={1.2} />
                    </div>
                    <CardTitle className="text-2xl font-default text-foreground mb-3">
                      {pathway.title}
                    </CardTitle>
                    <CardDescription className="text-base font-sans text-muted-foreground">
                      {pathway.description}
                    </CardDescription>
                  </CardHeader>

                  {}
                  <div className="absolute inset-x-0 top-0 bottom-[80px] z-20 bg-card/95 backdrop-blur-md p-8 flex flex-col justify-center opacity-0 group-hover:opacity-100 transition-opacity duration-500 pointer-events-none">
                    <h4 className="text-lg font-semibold font-default text-primary mb-6 flex items-center gap-2">
                      <Sparkles className="size-5" /> Service Features
                    </h4>
                    <ul className="space-y-4">
                      {pathwayHighlights.map((highlight, hIdx) => <li key={hIdx} data-index={hIdx} className="flex items-center gap-3 text-foreground translate-y-4 opacity-0 group-hover:translate-y-0 group-hover:opacity-100 transition-all duration-500" style={{
                    transitionDelay: `${hIdx * 150}ms`
                  }}>
                          <div className="size-1.5 rounded-lg bg-primary" />
                          <span className="font-sans font-medium">{highlight.text}</span>
                        </li>)}
                    </ul>
                  </div>

                  {}
                  <CardFooter className="relative z-30 mt-auto pt-6 border-t border-border/50 transition-colors duration-500 group-hover:border-primary/30 bg-card/50 backdrop-blur-sm">
                    <Button asChild variant="ghost" className="w-full justify-between group/btn hover:bg-primary/10 hover:text-primary font-sans rounded-md">
                      <Link to={pathway.link}>
                        Book Service
                        <ArrowRight className="size-4 transition-transform group-hover/btn:translate-x-1" />
                      </Link>
                    </Button>
                  </CardFooter>
                </Card>
              </div>;
        })}
        </div>
      </div>
    </section>;
}